import * as CollapsiblePrimitive from "@radix-ui/react-collapsible";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

/**
 * Collapsible — show / hide a single region.
 *
 * Use for ad-hoc "show more" / "advanced options" patterns where there is
 * exactly one toggleable section. If you have multiple sections grouped
 * together, prefer Accordion — it gives you a consistent visual chrome
 * (cards, chevrons, list semantics) for free.
 *
 * Built on Radix Collapsible: full ARIA wiring (aria-expanded /
 * aria-controls) and a measured-height CSS variable for smooth animation.
 */

export const Collapsible = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Root>
>(function Collapsible({ className, ...props }, ref) {
  return (
    <CollapsiblePrimitive.Root
      ref={ref}
      className={cx("cw-v2-collapsible", className)}
      {...props}
    />
  );
});
Collapsible.displayName = "Collapsible";

/**
 * Toggle that opens / closes the region. Renders as a button by default.
 * Use Radix's `asChild` to project the data-state onto your own component
 * (e.g. a Button or a chevron-style header).
 */
export const CollapsibleTrigger = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Trigger>
>(function CollapsibleTrigger({ asChild, children, className, ...props }, ref) {
  if (asChild) {
    return (
      <CollapsiblePrimitive.Trigger ref={ref} asChild className={className} {...props}>
        {children}
      </CollapsiblePrimitive.Trigger>
    );
  }

  return (
    <CollapsiblePrimitive.Trigger
      ref={ref}
      className={cx("cw-v2-collapsible__trigger", className)}
      {...props}
    >
      <span className="cw-v2-collapsible__trigger-label">{children}</span>
      <ChevronDown aria-hidden className="cw-v2-collapsible__chevron" />
    </CollapsiblePrimitive.Trigger>
  );
});
CollapsibleTrigger.displayName = "CollapsibleTrigger";

export const CollapsibleContent = React.forwardRef<
  React.ElementRef<typeof CollapsiblePrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof CollapsiblePrimitive.Content>
>(function CollapsibleContent({ children, className, ...props }, ref) {
  return (
    <CollapsiblePrimitive.Content
      ref={ref}
      className={cx("cw-v2-collapsible__content", className)}
      {...props}
    >
      {children}
    </CollapsiblePrimitive.Content>
  );
});
CollapsibleContent.displayName = "CollapsibleContent";
